import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, BarChart2, ChevronLeft, ChevronRight } from 'lucide-react';
import CalendarView from './CalendarView';
import { toast } from 'react-hot-toast';
import { format, subMonths, addMonths } from 'date-fns';
import { taskService } from '../../api/tasksApi';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../../constants/brandPalette';

const TimelineView = ({ project, milestones = [] }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Define loadTasks with useCallback
  const loadTasks = useCallback(async () => {
    if (!project?.id) return;
    
    setLoading(true);
    try {
      // Load tasks
      const taskPayload = await taskService.getProjectTasks(project.id);
      const projectTasks = Array.isArray(taskPayload)
        ? taskPayload
        : Array.isArray(taskPayload?.tasks)
          ? taskPayload.tasks
          : [];
      
      // Transform tasks to ensure consistent data structure
      const transformedTasks = projectTasks.map(task => ({
        id: task.id,
        title: task.title || task.name || '',
        description: task.description || '',
        startDate: task.startDate || task.createdAt || new Date().toISOString().split('T')[0],
        endDate: task.dueDate || task.endDate || '',
        progress: task.progress || 0,
        status: task.status || 'todo',
        assignee: task.assignee || task.assignedTo || null,
        milestoneId: task.milestoneId || null,
        evidence: task.evidence || [],
        createdAt: task.createdAt || new Date().toISOString(),
        updatedAt: task.updatedAt || new Date().toISOString()
      }));
      
      setTasks(transformedTasks);
      setError(null);
    } catch (error) {
      console.error('Error loading project data:', error);
      setError('Failed to load project data');
      toast.error('Failed to load project data');
    } finally {
      setLoading(false);
    }
  }, [project?.id]); // Add project.id as dependency

  // Call loadTasks when project changes
  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center rounded-2xl border" style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
        <div className="h-8 w-8 animate-spin rounded-full border-b-2" style={{ borderColor: BRAND_COLORS.primary }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-64 flex-col items-center justify-center rounded-2xl border" style={{ backgroundColor: 'rgba(220,38,38,0.08)', borderColor: 'rgba(220,38,38,0.18)' }}>
        <div className="text-red-600 mb-2">Error loading timeline data</div>
        <button
          onClick={() => {
            setError(null);
            loadTasks();
          }}
          className="rounded-lg px-4 py-2 text-white"
          style={{ backgroundColor: BRAND_COLORS.primary }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-2xl border p-2 sm:p-3" style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
      {/* Header - More compact */}
      <div className="border-b" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
        <div className="flex flex-col gap-2 px-2 py-2 sm:flex-row sm:items-center sm:justify-between sm:px-3">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-medium" style={{ color: NEUTRAL_COLORS.black }}>
              {format(selectedDate, 'MMMM yyyy')}
            </h2>
            <div className="flex">
              <button 
                onClick={() => setSelectedDate(subMonths(selectedDate, 1))} 
                className="rounded p-0.5"
                style={{ color: SYSTEM_COLORS.textMuted }}
                title="Previous month"
              >
                <ChevronLeft size={16} />
              </button>
              <button 
                onClick={() => setSelectedDate(addMonths(selectedDate, 1))} 
                className="rounded p-0.5"
                style={{ color: SYSTEM_COLORS.textMuted }}
                title="Next month"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
          <div className="flex w-full rounded-lg p-0.5 sm:w-auto" style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}>
            <button
              onClick={() => setView('calendar')}
              className="flex flex-1 items-center justify-center rounded px-2.5 py-1 text-sm sm:flex-none"
              style={{
                backgroundColor: view === 'calendar' ? SYSTEM_COLORS.shellSurface : 'transparent',
                color: view === 'calendar' ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
                boxShadow: view === 'calendar' ? SYSTEM_COLORS.shadowSoft : 'none',
              }}
            >
              <Calendar size={16} className="mr-1.5" />
              Calendar
            </button>
            <button
              onClick={() => setView('gantt')}
              className="flex flex-1 items-center justify-center rounded px-2.5 py-1 text-sm sm:flex-none"
              style={{
                backgroundColor: view === 'gantt' ? SYSTEM_COLORS.shellSurface : 'transparent',
                color: view === 'gantt' ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted,
                boxShadow: view === 'gantt' ? SYSTEM_COLORS.shadowSoft : 'none',
              }}
            >
              <BarChart2 size={16} className="mr-1.5" />
              Gantt
            </button>
          </div>
        </div>
      </div>

      {/* Content - Optimized spacing */}
      <div style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}>
        {milestones?.length > 0 && (
          <div className="border-b px-3 py-3 sm:px-4" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Project Milestones</h3>
                <p className="text-xs text-gray-500">Milestones reflected on the current timeline</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {milestones.slice(0, 3).map((milestone) => (
                  <span
                    key={milestone.id}
                    className="rounded-full border px-3 py-1 text-xs"
                    style={{
                      borderColor: SYSTEM_COLORS.borderSoft,
                      color: SYSTEM_COLORS.textMuted,
                      backgroundColor: SYSTEM_COLORS.shellSurfaceMuted,
                    }}
                  >
                    {milestone.title}
                  </span>
                ))}
                {milestones.length > 3 && (
                  <span className="text-xs text-gray-500">+{milestones.length - 3} more</span>
                )}
              </div>
            </div>
          </div>
        )}

        {view === 'gantt' ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <BarChart2 size={48} className="mb-4" style={{ color: SYSTEM_COLORS.textSoft }} />
            <h3 className="mb-2 text-lg font-medium" style={{ color: NEUTRAL_COLORS.black }}>Gantt View Coming Soon</h3>
            <p className="max-w-md" style={{ color: SYSTEM_COLORS.textMuted }}>
              We're working on bringing you a powerful Gantt chart view for better project timeline visualization.
            </p>
          </div>
        ) : tasks.length === 0 ? (
          <div className="py-6 text-center" style={{ color: SYSTEM_COLORS.textMuted }}>
            No tasks found.
          </div>
        ) : (
          <CalendarView
            project={project}
            tasks={tasks}
            timeframe={view}
            onEditTask={() => {}}
            onDeleteTask={() => {}}
            currentDate={selectedDate}
          />
        )}
      </div>
    </div>
  );
};

export default TimelineView; 